import type { PortalEffectConfig } from './types';

/**
 * Each icon gets its own portal soul.
 * Same gold-on-black frame — different presence within.
 */
export const PORTAL_PRESETS: Record<string, PortalEffectConfig> = {
  /**
   * Makarios III — Incense rising in a cathedral.
   * Slow upward drift, reverent, warm gold motes.
   */
  makarios: {
    particles: {
      count: 40,
      color: { r: 212, g: 183, b: 106 },
      minAlpha: 0.1,
      maxAlpha: 0.5,
      minSize: 1,
      maxSize: 3,
      speed: 12,
      drift: 15,
      direction: 'up',
      lifetime: 8,
      fadeIn: 0.15,
      fadeOut: 0.3,
    },
    edgeGlow: {
      enabled: true,
      color: { r: 197, g: 162, b: 88, a: 0.15 },
      width: 20,
      breathe: 0.46,
    },
  },

  /**
   * Bruce Lee — Disciplined chi radiating outward.
   * Controlled radial bursts, focused energy, warrior stillness.
   */
  bruceLee: {
    particles: {
      count: 55,
      color: { r: 226, g: 161, b: 68 },
      minAlpha: 0.08,
      maxAlpha: 0.45,
      minSize: 0.8,
      maxSize: 2.5,
      speed: 18,
      drift: 20,
      direction: 'radial',
      lifetime: 6,
      fadeIn: 0.1,
      fadeOut: 0.35,
    },
    edgeGlow: {
      enabled: true,
      color: { r: 202, g: 162, b: 65, a: 0.18 },
      width: 18,
      breathe: 0.46,
    },
  },

  /**
   * Giordano Bruno — Embers of a heretic's pyre.
   * Slow upward sparks, ashen warmth, defiant stillness.
   */
  giordanoBruno: {
    particles: {
      count: 35,
      color: { r: 180, g: 155, b: 100 },
      minAlpha: 0.06,
      maxAlpha: 0.38,
      minSize: 0.8,
      maxSize: 2.2,
      speed: 10,
      drift: 12,
      direction: 'up',
      lifetime: 10,
      fadeIn: 0.2,
      fadeOut: 0.4,
    },
    edgeGlow: {
      enabled: true,
      color: { r: 160, g: 145, b: 100, a: 0.12 },
      width: 16,
      breathe: 0.28,
    },
  },

  /**
   * Nikola Tesla — Electric discharge, crackling potential.
   * Fast upward sparks, bright flickers, restless current.
   */
  tesla: {
    particles: {
      count: 50,
      color: { r: 220, g: 195, b: 120 },
      minAlpha: 0.08,
      maxAlpha: 0.5,
      minSize: 0.6,
      maxSize: 2.0,
      speed: 22,
      drift: 18,
      direction: 'up',
      lifetime: 5,
      fadeIn: 0.08,
      fadeOut: 0.3,
    },
    edgeGlow: {
      enabled: true,
      color: { r: 210, g: 185, b: 100, a: 0.16 },
      width: 18,
      breathe: 0.38,
    },
  },

  /**
   * Buckminster Fuller — Geodesic calm, structural harmony.
   * Slow radial drift, quiet geometry, measured expansion.
   */
  fuller: {
    particles: {
      count: 30,
      color: { r: 190, g: 170, b: 110 },
      minAlpha: 0.06,
      maxAlpha: 0.35,
      minSize: 0.8,
      maxSize: 2.4,
      speed: 8,
      drift: 14,
      direction: 'radial',
      lifetime: 12,
      fadeIn: 0.2,
      fadeOut: 0.35,
    },
    edgeGlow: {
      enabled: true,
      color: { r: 185, g: 165, b: 100, a: 0.1 },
      width: 14,
      breathe: 0.25,
    },
  },

  /**
   * Placeholder for future icons — each gets a unique soul.
   * Spiral descent, like stars falling into prayer.
   */
  celestial: {
    particles: {
      count: 60,
      color: { r: 245, g: 215, b: 153 },
      minAlpha: 0.05,
      maxAlpha: 0.35,
      minSize: 0.5,
      maxSize: 2,
      speed: 8,
      drift: 25,
      direction: 'spiral',
      lifetime: 12,
      fadeIn: 0.2,
      fadeOut: 0.4,
    },
    edgeGlow: {
      enabled: true,
      color: { r: 245, g: 215, b: 153, a: 0.1 },
      width: 15,
      breathe: 0.3,
    },
  },
};
